/**
 * Plays a song on an AudioContext (real-time or offline): the sequencer's notes become
 * instrument voices, everything goes through the player's own filter and fade bus, so one
 * player can fade out while the next one starts (a new level, a restart).
 *
 * Turbo and slow motion ("time scale") do more than change the tempo: like a tape played
 * faster or slower the music also goes up or down in pitch (half as much as a real tape
 * would, so it stays musical), and slow motion closes a low-pass filter, as if heard from
 * under water.
 *
 * A player keeps time on its own (`start`) or on the game's clock (`sync`, see Sequencer):
 * the local racer's music begins with every attempt, its first beat on the attempt's first
 * tick, and stays on the game's beat through turbo, slow motion and lost frames.
 */
import { type LiveVoices, type SynthStats, scheduleVoices } from '../synth';
import type { Song, SongEvent, SongVoice } from './composer';
import { arpVoices, bassVoices, drumVoices, leadVoices } from './instruments';
import { Sequencer } from './sequencer';

/** Share of a real tape's pitch change: 0.5 = turbo (x1.5) ≈ +3.5 semitones. */
const TAPE_DEPTH = 0.5;
/** Notes are shortened a little, so repeated notes stay separate. */
const LEGATO = 0.9;
const FADE_IN = 0.03;
const PAUSE_FADE = 0.06;
const MIN_BPM = 40;
const MAX_BPM = 300;

export interface MusicPlayerOptions {
  /** Parts left out (the offline checks render the drums alone, for example). */
  mute?: ReadonlySet<SongVoice>;
  stats?: SynthStats;
}

/** Pitch change of the tape effect for a time scale, in cents. */
export function tapeCents(scale: number): number {
  return scale > 0 ? 1200 * Math.log2(scale) * TAPE_DEPTH : 0;
}

/** Low-pass cutoff for a time scale: wide open at normal speed and faster, muffled when slow. */
export function cutoffFor(scale: number): number {
  if (!(scale < 1)) return 20000;
  return Math.min(20000, Math.max(900, 20000 * scale ** 5));
}

export class MusicPlayer {
  /** Fade bus: connect/disconnect the player here. */
  readonly bus: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly sequencer: Sequencer;
  private readonly emit = (event: SongEvent, time: number, stepSeconds: number, scale: number) =>
    this.playNote(event, time, stepSeconds, scale);
  /** When the song's first bar begins; -1 before it is placed. */
  private beginsAt = -1;
  /** Notes still sounding, cut short when the player stops. */
  private readonly live: LiveVoices = new Map();
  /** The time scale the filter was last set for. */
  private filterScale = 1;

  constructor(
    private readonly ctx: BaseAudioContext,
    output: AudioNode,
    private readonly noise: AudioBuffer,
    private readonly song: Song,
    bpm: number,
    private readonly options: MusicPlayerOptions = {},
  ) {
    const tempo = Number.isFinite(bpm) ? Math.min(MAX_BPM, Math.max(MIN_BPM, bpm)) : 120;
    this.sequencer = new Sequencer(song, tempo);
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 20000;
    this.filter.Q.value = 0.8;
    this.bus = ctx.createGain();
    this.bus.gain.value = 0;
    this.filter.connect(this.bus);
    this.bus.connect(output);
  }

  get timing(): Sequencer {
    return this.sequencer;
  }

  /** Placed on the game's clock (see sync). */
  get isSynced(): boolean {
    return this.sequencer.isSynced;
  }

  /** Starts the loop from its first bar at `time`, keeping time on its own. */
  start(time: number): void {
    this.beginsAt = time;
    this.fadeIn(time, time);
    this.sequencer.start(time);
  }

  /**
   * Plays on the game's clock: at `time` the song should be `position` seconds in (at its
   * base tempo, negative before it begins), moving at `scale` (see Sequencer.sync). The first
   * call places the song, and so does the first call after a pause (a hidden page): silent
   * until then, its first beat at full volume (the fade-in ends there; a song placed mid-way
   * fades in at once). `now` = the context's time.
   */
  sync(time: number, position: number, scale: number, now: number): void {
    const playing = this.sequencer.isSynced && this.sequencer.isRunning;
    this.sequencer.sync(time, position, scale);
    const begins = time - position / clampScale(scale);
    // Placed for the first time or after a pause, or moved before it began (a frame came late).
    if (!playing || (this.beginsAt > now && Math.abs(begins - this.beginsAt) > 0.01)) {
      this.beginsAt = begins;
      this.fadeIn(Math.max(now, begins - FADE_IN), now);
    }
    const clamped = clampScale(scale);
    if (clamped !== this.filterScale) {
      this.filterScale = clamped;
      this.filter.frequency.setTargetAtTime(cutoffFor(clamped), now, 0.15);
    }
  }

  /** Schedules every note starting before `until` (`now` = the context's current time). */
  scheduleUntil(until: number, now: number = until): void {
    this.sequencer.advance(until, now, this.emit);
  }

  /** Tempo follows `scale` (gliding); pitch and filter follow from the next notes on. */
  setTimeScale(scale: number, at: number, immediate = false): void {
    this.sequencer.setTimeScale(scale, immediate);
    this.filterScale = clampScale(scale);
    const cutoff = cutoffFor(this.filterScale);
    if (immediate) this.filter.frequency.setValueAtTime(cutoff, at);
    else this.filter.frequency.setTargetAtTime(cutoff, at, 0.15);
  }

  /**
   * Stops scheduling and fades out quickly; `resume` continues where it stopped (on the
   * game's clock: the next `sync` places it again, where the game is by then).
   */
  pause(at: number): void {
    this.sequencer.pause();
    this.bus.gain.cancelScheduledValues(at);
    this.bus.gain.setTargetAtTime(0, at, PAUSE_FADE / 3);
  }

  resume(at: number): void {
    this.bus.gain.cancelScheduledValues(at);
    this.bus.gain.setTargetAtTime(1, at, FADE_IN);
    this.sequencer.resume(at + 0.02);
  }

  /**
   * Fades out over `seconds` from `at` and stops scheduling; notes ringing on past the fade
   * (a long note or bell) stop with it. Disconnect afterwards.
   */
  stop(at: number, seconds: number): void {
    this.sequencer.pause();
    const end = at + Math.max(0.01, seconds);
    const gain = this.bus.gain;
    gain.cancelScheduledValues(at);
    gain.setValueAtTime(gain.value, at);
    gain.linearRampToValueAtTime(0, end);
    const cut = end + 0.02;
    for (const [source, stopsAt] of this.live) {
      if (stopsAt <= cut) continue;
      try {
        source.stop(cut);
      } catch {
        // An engine that refuses a second stop(): the note ends on its own, under the fade.
      }
    }
  }

  disconnect(): void {
    this.bus.disconnect();
    this.filter.disconnect();
  }

  /** Silent until `time`, then a short fade-in (no click); `now` = the context's time. */
  private fadeIn(time: number, now: number): void {
    const gain = this.bus.gain;
    gain.cancelScheduledValues(Math.min(now, time));
    gain.setValueAtTime(0, Math.min(now, time));
    gain.setValueAtTime(0, time);
    gain.linearRampToValueAtTime(1, time + FADE_IN);
  }

  private playNote(event: SongEvent, time: number, stepSeconds: number, scale: number): void {
    if (this.options.mute?.has(event.voice)) return;
    const seconds = event.length * stepSeconds * LEGATO;
    const style = this.song.style;
    let voices: ReturnType<typeof drumVoices>;
    switch (event.voice) {
      case 'lead':
        voices = leadVoices(style.lead, event.midi, seconds, event.velocity);
        break;
      case 'arp':
        voices = arpVoices(style.arp, event.midi, seconds, event.velocity);
        break;
      case 'bass':
        voices = bassVoices(style.bass, event.midi, seconds, event.velocity);
        break;
      default:
        voices = drumVoices(style.kit, event.voice, event.velocity, event.midi);
    }
    scheduleVoices(
      this.ctx,
      this.filter,
      this.noise,
      voices,
      // A note a moment late (allowed on the game's clock) plays at once; Web Audio refuses
      // times before the context's start.
      Math.max(0, time),
      tapeCents(scale),
      this.options.stats,
      this.live,
    );
  }
}

function clampScale(scale: number): number {
  return Number.isFinite(scale) ? Math.min(4, Math.max(0.25, scale)) : 1;
}
